import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useI18n } from '../i18n'
import { useAuthStore } from '../store/useAuthStore'
import { useSongStore } from '../store/useSongStore'
import { SECTION_TYPES } from '../config/songbook'
import { AuthArea } from '../components/AuthArea'
import { NewSongDialog } from '../components/songbook/NewSongDialog'
import { exportData, importData } from '../lib/exportImport'
import { OnlineIndicator } from '../components/offline/OnlineIndicator'
import { OFFLINE_ENABLED } from '../config/offline'

export function SongListPage() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { collectionId } = useParams<{ collectionId?: string }>()
  const user = useAuthStore((s) => s.user)
  const {
    songs,
    collections,
    playlists,
    isLoading,
    fetchSongs,
    fetchCollections,
    fetchPlaylists,
    deleteCollection,
    renameCollection,
    toggleShareCollection,
    createPlaylist,
    addSongsToPlaylist,
  } = useSongStore()
  const [showNewSong, setShowNewSong] = useState(false)
  const [showPlaylistPicker, setShowPlaylistPicker] = useState<string[] | null>(null)
  const [statusMsg, setStatusMsg] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (user) {
      void fetchCollections()
      void fetchSongs()
      void fetchPlaylists()
    }
  }, [user]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteCollection = async (id: string) => {
    const songCount = songs.filter((s) => s.collectionId === id).length
    if (songCount > 0) {
      alert(t('songbook.deleteCollectionNotEmpty'))
      return
    }
    if (confirm(t('songbook.deleteCollectionConfirm'))) {
      await deleteCollection(id)
      navigate('/songs')
    }
  }

  const handleExport = async () => {
    try {
      await exportData()
      setStatusMsg(t('songbook.exportSuccess'))
      setTimeout(() => setStatusMsg(null), 3000)
    } catch (e) {
      setStatusMsg(String(e))
      setTimeout(() => setStatusMsg(null), 5000)
    }
  }

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      const result = await importData(file, user!.id)
      setStatusMsg(
        t('songbook.importSuccess', {
          collections: String(result.collections),
          songs: String(result.songs),
          playlists: String(result.playlists),
        })
      )
      await fetchCollections(true)
      await fetchSongs(true)
      await fetchPlaylists(true)
      setTimeout(() => setStatusMsg(null), 5000)
    } catch (err) {
      console.error('Import error:', err)
      setStatusMsg(t('songbook.importError') + ' ' + String(err))
      setTimeout(() => setStatusMsg(null), 8000)
    }

    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  // If a collectionId is in the URL, show that collection's songs
  const activeCollection = collectionId
    ? collections.find((c) => c.id === collectionId)
    : null

  return (
    <div className="flex min-h-full flex-col">
      <header className="flex items-center justify-between px-4 py-3">
        {activeCollection ? (
          <div className="flex items-center gap-2">
            <button
              className="flex items-center gap-2 text-text-muted hover:text-text"
              onClick={() => navigate('/songs')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
              <span className="text-lg font-bold text-text">{activeCollection.name}</span>
            </button>
            <OnlineIndicator />
          </div>
        ) : (
          <h1 className="flex items-center gap-2 text-lg font-bold text-text">
            {t('songbook.title')}
            <OnlineIndicator />
          </h1>
        )}
        <AuthArea />
      </header>

      <main className="flex-1 px-4 pb-4">
        {!user ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <p className="text-text-muted">{t('songbook.loginRequired')}</p>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-20">
            <p className="text-text-muted">{t('general.loading')}</p>
          </div>
        ) : activeCollection ? (
          /* ── Collection detail: songs list ── */
          <CollectionDetailView
            collection={activeCollection}
            songs={songs.filter((s) => s.collectionId === activeCollection.id)}
            t={t}
            statusMsg={statusMsg}
            onOpenSong={(id) => navigate(`/songs/${id}`)}
            onDeleteCollection={() => handleDeleteCollection(activeCollection.id)}
            onRenameCollection={(name) => renameCollection(activeCollection.id, name)}
            onShare={async () => {
              const token = await toggleShareCollection(activeCollection.id)
              if (token) {
                const url = `${window.location.origin}/share/${token}`
                await navigator.clipboard.writeText(url)
                setStatusMsg(t('songbook.shareCopied'))
                setTimeout(() => setStatusMsg(null), 3000)
              }
            }}
            shareActive={!!activeCollection.shareToken}
            onNewSong={() => setShowNewSong(true)}
            onAddToPlaylist={(songIds) => setShowPlaylistPicker(songIds)}
          />
        ) : (
          /* ── Collections overview ── */
          <>
            <button
              className="mb-4 w-full rounded-lg bg-primary py-3 text-center font-semibold text-white active:bg-primary-dark"
              onClick={() => setShowNewSong(true)}
            >
              + {t('songbook.newSong')}
            </button>

            {statusMsg && (
              <div className="mb-4 rounded-lg bg-success/20 px-4 py-2 text-center text-sm text-success">
                {statusMsg}
              </div>
            )}

            {collections.length === 0 ? (
              <p className="py-10 text-center text-text-muted">{t('songbook.noCollections')}</p>
            ) : (
              <div className="space-y-3">
                {collections.map((collection) => {
                  const count = songs.filter((s) => s.collectionId === collection.id).length
                  return (
                    <div
                      key={collection.id}
                      className="flex items-center justify-between rounded-lg border border-surface-lighter bg-surface-light p-4 active:bg-surface-lighter cursor-pointer"
                      onClick={() => navigate(`/songs/collection/${collection.id}`)}
                    >
                      <div className="min-w-0 flex-1">
                        <h2 className="text-base font-semibold text-text">{collection.name}</h2>
                        {collection.artist && (
                          <p className="text-sm text-text-muted">{collection.artist}</p>
                        )}
                        <p className="text-xs text-text-muted">
                          {t('songbook.songsCount', { count: String(count) })}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          className={`rounded p-1.5 ${collection.shareToken ? 'text-success' : 'text-text-muted'} hover:text-primary`}
                          onClick={async (e) => {
                            e.stopPropagation()
                            const token = await toggleShareCollection(collection.id)
                            if (token) {
                              const url = `${window.location.origin}/share/${token}`
                              await navigator.clipboard.writeText(url)
                              setStatusMsg(t('songbook.shareCopied'))
                              setTimeout(() => setStatusMsg(null), 3000)
                            }
                          }}
                          title={collection.shareToken ? t('songbook.shareStop') : t('songbook.share')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                          </svg>
                        </button>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-muted"><path d="m9 18 6-6-6-6"/></svg>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Playlists */}
            {playlists.length > 0 && (
              <>
                <h2 className="mt-6 mb-2 text-sm font-semibold text-text-muted uppercase tracking-wide">
                  {t('songbook.playlists')}
                </h2>
                <div className="space-y-2">
                  {playlists.map((pl) => (
                    <div
                      key={pl.id}
                      className="flex items-center justify-between rounded-lg border border-surface-lighter bg-surface-light p-4 active:bg-surface-lighter cursor-pointer"
                      onClick={() => navigate(`/playlist/${pl.id}`)}
                    >
                      <div className="min-w-0 flex-1">
                        <h2 className="text-base font-semibold text-text">{pl.name}</h2>
                        <p className="text-xs text-text-muted">
                          {t('songbook.songsCount', { count: String(pl.songIds.length) })}
                        </p>
                      </div>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0 text-text-muted"><path d="m9 18 6-6-6-6"/></svg>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Offline songs link */}
            {user && OFFLINE_ENABLED && (
              <button
                className="mt-8 w-full rounded-lg border border-surface-lighter py-2.5 text-center text-sm font-medium text-text-muted active:bg-surface-lighter"
                onClick={() => navigate('/offline')}
              >
                {t('offline.openOfflinePage')}
              </button>
            )}

            {/* Export / Import */}
            {user && (
              <div className={`${OFFLINE_ENABLED ? 'mt-2' : 'mt-8'} flex gap-2 border-t border-surface-lighter pt-4`}>
                <button
                  className="flex-1 rounded-lg border border-surface-lighter py-2.5 text-center text-sm font-medium text-text-muted active:bg-surface-lighter"
                  onClick={handleExport}
                >
                  ↓ {t('songbook.export')}
                </button>
                <button
                  className="flex-1 rounded-lg border border-surface-lighter py-2.5 text-center text-sm font-medium text-text-muted active:bg-surface-lighter"
                  onClick={() => fileInputRef.current?.click()}
                >
                  ↑ {t('songbook.import')}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleImport}
                  className="hidden"
                />
              </div>
            )}
          </>
        )}
      </main>

      {showNewSong && (
        <NewSongDialog
          defaultCollectionId={collectionId}
          onCreated={(songId) => {
            setShowNewSong(false)
            navigate(`/songs/${songId}`)
          }}
          onClose={() => setShowNewSong(false)}
        />
      )}

      {showPlaylistPicker && (
        <PlaylistPickerDialog
          playlists={playlists}
          songIds={showPlaylistPicker}
          onSelect={async (playlistId) => {
            await addSongsToPlaylist(playlistId, showPlaylistPicker)
            setShowPlaylistPicker(null)
            setStatusMsg(t('songbook.addedToPlaylist'))
            setTimeout(() => setStatusMsg(null), 3000)
          }}
          onCreate={async (name) => {
            const id = await createPlaylist(name, showPlaylistPicker)
            setShowPlaylistPicker(null)
            if (id) {
              setStatusMsg(t('songbook.playlistCreated'))
              setTimeout(() => setStatusMsg(null), 3000)
            }
          }}
          onClose={() => setShowPlaylistPicker(null)}
          t={t}
        />
      )}
    </div>
  )
}

/* ── Collection Detail View ── */

function CollectionDetailView({
  collection,
  songs,
  t,
  statusMsg,
  onOpenSong,
  onDeleteCollection,
  onRenameCollection,
  onShare,
  shareActive,
  onNewSong,
  onAddToPlaylist,
}: {
  collection: { id: string; name: string; artist: string }
  songs: { id: string; title: string; elements: unknown[]; updatedAt: string }[]
  t: ReturnType<typeof useI18n>['t']
  statusMsg: string | null
  onOpenSong: (id: string) => void
  onDeleteCollection: () => void
  onRenameCollection: (name: string) => void
  onShare: () => void
  shareActive: boolean
  onNewSong: () => void
  onAddToPlaylist: (songIds: string[]) => void
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [editingName, setEditingName] = useState(false)

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <>
      {/* Collection info */}
      {editingName ? (
        <input
          type="text"
          defaultValue={collection.name}
          autoFocus
          className="mb-2 w-full rounded border border-primary bg-surface px-3 py-1.5 text-base font-semibold text-text focus:outline-none"
          onBlur={(e) => {
            const val = e.target.value.trim()
            if (val && val !== collection.name) onRenameCollection(val)
            setEditingName(false)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
            if (e.key === 'Escape') setEditingName(false)
          }}
        />
      ) : (
        <p
          className="mb-1 cursor-pointer text-sm text-text-muted hover:text-text"
          onDoubleClick={() => setEditingName(true)}
          title={t('songbook.renameCollection')}
        >
          {collection.artist || t('songbook.tapToRename')}
        </p>
      )}

      {/* Action bar */}
      <div className="mb-4 flex gap-2">
        <button
          className="flex-1 rounded-lg bg-primary py-2.5 text-center text-sm font-semibold text-white active:bg-primary-dark"
          onClick={onNewSong}
        >
          + {t('songbook.newSong')}
        </button>
        <button
          className="rounded-lg border border-surface-lighter px-3 py-2.5 text-text-muted hover:text-text"
          onClick={() => setEditingName(true)}
          title={t('songbook.renameCollection')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"/></svg>
        </button>
        <button
          className={`rounded-lg px-3 py-2.5 ${shareActive ? 'bg-success/20 text-success' : 'border border-surface-lighter text-text-muted'}`}
          onClick={onShare}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
        </button>
        <button
          className="rounded-lg border border-surface-lighter px-3 py-2.5 text-text-muted hover:text-error"
          onClick={onDeleteCollection}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
        </button>
      </div>

      {statusMsg && (
        <div className="mb-4 rounded-lg bg-success/20 px-4 py-2 text-center text-sm text-success">
          {statusMsg}
        </div>
      )}

      {/* Songs with checkboxes */}
      {songs.length === 0 ? (
        <p className="py-10 text-center text-text-muted">{t('songbook.noSongs')}</p>
      ) : (
        <div className="space-y-2">
          {(songs as import('../types/songbook').Song[]).map((song) => (
            <div
              key={song.id}
              className="flex items-center gap-3 rounded-lg border border-surface-lighter bg-surface-light p-4 active:bg-surface-lighter"
            >
              <input
                type="checkbox"
                checked={selected.has(song.id)}
                onChange={() => toggleSelect(song.id)}
                className="shrink-0 accent-primary"
              />
              <div
                className="min-w-0 flex-1 cursor-pointer"
                onClick={() => onOpenSong(song.id)}
              >
                <h3 className="truncate text-lg font-semibold text-text">
                  {song.title || t('songbook.untitled')}
                </h3>
                <SongPreview song={song as import('../types/songbook').Song} t={t} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add to playlist button */}
      {selected.size > 0 && (
        <button
          className="mt-4 w-full rounded-lg bg-primary py-3 text-center text-sm font-semibold text-white active:bg-primary-dark"
          onClick={() => {
            onAddToPlaylist(Array.from(selected))
            setSelected(new Set())
          }}
        >
          {t('songbook.addToPlaylist', { count: String(selected.size) })}
        </button>
      )}
    </>
  )
}

/* ── Playlist picker dialog ── */

function PlaylistPickerDialog({
  playlists,
  songIds,
  onSelect,
  onCreate,
  onClose,
  t,
}: {
  playlists: import('../types/songbook').Playlist[]
  songIds: string[]
  onSelect: (playlistId: string) => void
  onCreate: (name: string) => void
  onClose: () => void
  t: ReturnType<typeof useI18n>['t']
}) {
  const [newName, setNewName] = useState('')

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center" onClick={onClose}>
      <div
        className="w-full rounded-t-2xl border-t border-surface-lighter bg-surface-light p-5 pb-8 sm:max-w-md sm:rounded-2xl sm:border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-surface-lighter sm:hidden" />
        <h3 className="mb-4 text-lg font-bold text-text">
          {t('songbook.selectPlaylist')}
        </h3>
        <p className="mb-3 text-xs text-text-muted">
          {t('songbook.addToPlaylist', { count: String(songIds.length) })}
        </p>

        {playlists.length > 0 && (
          <div className="mb-4 space-y-2">
            {playlists.map((p) => (
              <button
                key={p.id}
                className="w-full rounded-lg border border-surface-lighter bg-surface p-3 text-left text-sm font-medium text-text active:bg-surface-lighter"
                onClick={() => onSelect(p.id)}
              >
                {p.name}
                <span className="ml-2 text-xs text-text-muted">
                  ({p.songIds.length} Songs)
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t('songbook.newPlaylistName')}
            className="flex-1 rounded-lg border-2 border-surface-lighter bg-surface px-3 py-2.5 text-sm text-text placeholder-text-muted focus:border-primary focus:outline-none"
          />
          <button
            className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white active:bg-primary-dark disabled:opacity-50"
            disabled={!newName.trim()}
            onClick={() => onCreate(newName.trim())}
          >
            {t('songbook.createPlaylist')}
          </button>
        </div>

        <button
          className="mt-3 w-full rounded-lg border border-surface-lighter py-2.5 text-sm text-text-muted active:bg-surface-lighter"
          onClick={onClose}
        >
          {t('general.cancel')}
        </button>
      </div>
    </div>
  )
}

function SongPreview({ song, t }: { song: import('../types/songbook').Song; t: ReturnType<typeof useI18n>['t'] }) {
  const preview = song.elements
    .slice(0, 6)
    .map((el) => {
      if (el.type === 'section') {
        const cfg = SECTION_TYPES.find((s) => s.id === el.sectionTypeId)
        return el.customName || (cfg ? t(cfg.i18nKey as Parameters<typeof t>[0]) : el.sectionTypeId)
      }
      if (el.type === 'instrument') return ''
      if (el.type === 'chord') return el.name
      if (el.type === 'tablature') return 'TAB'
      if (el.type === 'image') return 'IMG'
      if (el.type === 'midi') return 'MIDI'
      return ''
    })
    .filter(Boolean)
    .join(' / ')
  if (!preview) return null
  return <p className="mt-1 truncate text-sm text-text-muted">{preview}</p>
}
